import { useRef, useState } from "react";
import type { FormEvent } from "react";
import { App, Input } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { changeOwnCode, changeOwnPassword } from "../../api/credentials";
import { useFocusOnOpen } from "../../hooks/useFocusOnOpen";
import { useViewport } from "../../hooks/useViewport";
import { getApiErrorMessage } from "../../utils/translations";

export type CredentialKind = "code" | "password";

type FieldName = "current" | "next" | "confirm";
type FieldValues = Record<FieldName, string>;
type FieldErrors = Partial<Record<FieldName, string>>;

const EMPTY_VALUES: FieldValues = { current: "", next: "", confirm: "" };

// Огледало на правилата в org админа (UserModal): новият код е до 64 знака, без
// интервали и без < > " ' ` \ (махат се още при писане); новата парола е поне
// 3 знака и само на латиница (кирилицата се маха с подсказка).
const CODE_FORBIDDEN_CHARACTERS = /[\s<>"'`\\]/g;
const CODE_MAX_LENGTH = 64;
const CYRILLIC_CHARACTERS = /[Ѐ-ӿ]/g;
const PASSWORD_MIN_LENGTH = 3;

// Ключовете на текстовете по вид — полетата и проверките са едни и същи.
const TEXT_KEYS = {
  code: {
    current: "credentials.modal.currentCode",
    next: "credentials.modal.newCode",
    confirm: "credentials.modal.confirmCode",
    currentRequired: "credentials.modal.currentCodeRequired",
    nextRequired: "credentials.modal.newCodeRequired",
    confirmRequired: "credentials.modal.confirmCodeRequired",
    mismatch: "credentials.modal.codeMismatch",
    saved: "credentials.modal.codeSaved",
  },
  password: {
    current: "credentials.modal.currentPassword",
    next: "credentials.modal.newPassword",
    confirm: "credentials.modal.confirmPassword",
    currentRequired: "credentials.modal.currentPasswordRequired",
    nextRequired: "credentials.modal.newPasswordRequired",
    confirmRequired: "credentials.modal.confirmPasswordRequired",
    mismatch: "credentials.modal.passwordMismatch",
    saved: "credentials.modal.passwordSaved",
  },
} as const;

interface ChangeCredentialsFormProps {
  kind: CredentialKind;
  /** null — човекът има само едното, превключвателят Код/Парола не се показва. */
  onKindChange: ((kind: CredentialKind) => void) | null;
  onDone: () => void;
}

export default function ChangeCredentialsForm({
  kind,
  onKindChange,
  onDone,
}: ChangeCredentialsFormProps) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { isMobile, isNarrow } = useViewport();
  const [values, setValues] = useState<FieldValues>(EMPTY_VALUES);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const textKeys = TEXT_KEYS[kind];
  // Първото поле взима фокуса при отваряне (autoFocus не издържа в antd Modal)
  // и след смяна на раздела Код/Парола.
  const firstInputRef = useRef<HTMLInputElement | null>(null);
  useFocusOnOpen(true, firstInputRef);

  const switchKind = (nextKind: CredentialKind) => {
    if (nextKind === kind || !onKindChange) return;
    onKindChange(nextKind);
    setValues(EMPTY_VALUES);
    setErrors({});
    // Полетата са с ключ по вид — новото първо поле се закача в следващия кадър.
    window.requestAnimationFrame(() => firstInputRef.current?.focus());
  };

  const handleChange = (field: FieldName, rawValue: string) => {
    let value = rawValue;
    let error: string | undefined;
    // Текущата стойност се праща както е въведена — проверява я сървърът.
    if (field !== "current" && kind === "code") {
      value = rawValue.replace(CODE_FORBIDDEN_CHARACTERS, "");
    } else if (field !== "current" && kind === "password") {
      value = rawValue.replace(CYRILLIC_CHARACTERS, "");
      if (value !== rawValue) error = t("credentials.modal.passwordLatin");
    }
    setValues((previous) => ({ ...previous, [field]: value }));
    setErrors((previous) => ({ ...previous, [field]: error }));
  };

  const validate = (): FieldErrors => {
    const found: FieldErrors = {};
    if (!values.current) found.current = t(textKeys.currentRequired);
    if (!values.next) found.next = t(textKeys.nextRequired);
    else if (kind === "password" && values.next.length < PASSWORD_MIN_LENGTH)
      found.next = t("credentials.modal.passwordMin");
    if (!values.confirm) found.confirm = t(textKeys.confirmRequired);
    else if (values.confirm !== values.next) found.confirm = t(textKeys.mismatch);
    return found;
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting) return;
    const found = validate();
    if (Object.keys(found).length > 0) {
      setErrors(found);
      return;
    }
    setSubmitting(true);
    try {
      if (kind === "code") await changeOwnCode(values.current, values.next);
      else await changeOwnPassword(values.current, values.next);
      message.success(t(textKeys.saved));
      onDone();
    } catch (error) {
      // 400 — грешна текуща стойност, 409 — кодът е зает: под полето им.
      // Останалите (403 — деактивиран след 5 грешни опита, мрежа) — като съобщение.
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      const text = getApiErrorMessage(error, t("credentials.modal.saveError"));
      if (status === 400) setErrors({ current: text });
      else if (status === 409) setErrors({ next: text });
      else message.error(text);
      setSubmitting(false);
    }
  };

  const fields: {
    name: FieldName;
    autoComplete: string;
  }[] = [
    { name: "current", autoComplete: kind === "password" ? "current-password" : "off" },
    { name: "next", autoComplete: kind === "password" ? "new-password" : "off" },
    { name: "confirm", autoComplete: kind === "password" ? "new-password" : "off" },
  ];

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className={`flex flex-col gap-4 ${isNarrow ? "p-4" : "px-6 pt-5 pb-6"}`}>
        {onKindChange && (
          <div className="grid grid-cols-2 gap-1 rounded-[10px] bg-shade-1 p-1">
            {(["code", "password"] as const).map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => switchKind(option)}
                className={`h-9 rounded-lg border-none text-[13px] font-semibold transition-colors duration-150 ${
                  kind === option
                    ? "cursor-default bg-surface text-ink shadow-sm"
                    : "cursor-pointer bg-transparent text-muted hover:text-ink"
                }`}
              >
                {option === "code"
                  ? t("credentials.modal.tabCode")
                  : t("credentials.modal.tabPassword")}
              </button>
            ))}
          </div>
        )}

        {fields.map((field, index) => (
          <div key={`${kind}-${field.name}`} className="flex flex-col gap-2">
            <label
              htmlFor={`credentials-${field.name}`}
              className="text-[10.5px] font-bold uppercase leading-[1.2] tracking-[1.2px] text-muted"
            >
              {t(textKeys[field.name])}
            </label>
            <Input.Password
              id={`credentials-${field.name}`}
              size="large"
              value={values[field.name]}
              onChange={(event) => handleChange(field.name, event.target.value)}
              status={errors[field.name] ? "error" : undefined}
              maxLength={kind === "code" ? CODE_MAX_LENGTH : 128}
              autoComplete={field.autoComplete}
              ref={
                index === 0
                  ? (instance) => {
                      firstInputRef.current = instance?.input ?? null;
                    }
                  : undefined
              }
              className="!rounded-[10px]"
            />
            {errors[field.name] && (
              <div role="alert" className="text-[12px] leading-[1.45] text-danger-ink">
                {errors[field.name]}
              </div>
            )}
          </div>
        ))}
      </div>

      <div
        className={`flex gap-2 border-t border-shade-2 ${
          isNarrow ? "px-4 py-3" : "px-6 py-4"
        } ${isMobile ? "flex-col-reverse" : "justify-end"}`}
      >
        <button
          type="button"
          onClick={onDone}
          disabled={submitting}
          className="h-11 cursor-pointer rounded-[10px] border border-shade-2 bg-transparent px-[18px] text-[13.5px] font-semibold text-body transition-colors duration-150 hover:bg-shade-1 disabled:cursor-not-allowed"
        >
          {t("common.cancel")}
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="flex h-11 cursor-pointer items-center justify-center gap-2 rounded-[10px] border-none bg-brand px-[22px] text-[13.5px] font-semibold text-white transition-colors duration-150 hover:bg-brand-deep disabled:cursor-not-allowed disabled:opacity-70"
        >
          {submitting && <LoadingOutlined />}
          {t("common.save")}
        </button>
      </div>
    </form>
  );
}
